import React, { useRef, useState } from 'react';
// Native MUI Components
import { Button, Typography, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
// Native MUI Dialog
import { MuiDialog } from '@/components/ui';
import { Trip } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';
import { ExportService } from '@/services/exportService';
import { ImportService } from '@/services/importService';

interface ImportExportFeatureProps {
  trip?: Trip; // Make trip optional for collection-level operations
  onImportComplete?: () => void;
  trigger?: React.ReactNode;
}

const ImportExportFeature: React.FC<ImportExportFeatureProps> = ({ trip, onImportComplete, trigger }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();

  const exportData = async () => {
    if (!trip?.id) {
      toast({
        title: t('importExport.exportFailed'),
        description: t('importExport.noTripDataAvailable'),
        variant: "destructive",
      });
      return;
    }

    try {
      // Export trip data using the service
      const exportData = await ExportService.exportTripData(trip);
      const filename = ExportService.generateExportFilename(trip);
      
      // Download the export
      ExportService.downloadExportData(exportData, filename);
      
      toast({
        title: t('importExport.exportSuccessful'),
        description: t('importExport.comprehensiveBackupExported', {
          travellers: exportData.subcollections.travellers.length,
          apartments: exportData.subcollections.apartments.length,
          signUps: exportData.subcollections.signUps.length,
        }),
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('importExport.exportFailed'),
        description: t('importExport.failedToExportTrip', { error: (error as Error).message }),
        variant: "destructive",
      });
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const importResult = await ImportService.readImportFile(file);
      
      if (!importResult.success) {
        toast({
          title: t('importExport.importFailed'),
          description: t('importExport.failedToImportData', { error: importResult.error }),
          variant: "destructive",
        });
        return;
      }

      // Handle different import types
      switch (importResult.type) {
        case 'comprehensive': {
          const exportDate = importResult.metadata?.exportTimestamp?.split('T')[0] || '';
          toast({
            title: t('importExport.importFormatDetected'),
            description: t('importExport.comprehensiveBackupDetected', { date: exportDate }),
          });
          
          // Log the comprehensive import data for now
          console.log("Comprehensive trip import detected:", {
            trip: importResult.metadata?.tripName,
            travellers: importResult.metadata?.travellersCount,
            apartments: importResult.metadata?.apartmentsCount,
            signUps: importResult.metadata?.signUpsCount,
            exportTimestamp: importResult.metadata?.exportTimestamp
          });
          break;
        }

        case 'legacy-trip':
          console.warn("Legacy single trip import detected. This format is deprecated.");
          toast({
            title: t('importExport.legacyFormatDetected'),
            description: t('importExport.legacyTripFormatDetected'),
          });
          break;

        case 'legacy-collection':
          console.log("Legacy trip collection import:", importResult.metadata?.tripName);
          toast({
            title: t('importExport.legacyFormatDetected'),
            description: t('importExport.legacyCollectionFormatDetected'),
          });
          break;
      }

      if (onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      toast({
        title: t('importExport.importFailed'),
        description: t('importExport.failedToImportData', { error: (error as Error).message }),
        variant: "destructive",
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {trigger ? (
        <Box onClick={() => setOpen(true)}>
          {trigger}
        </Box>
      ) : (
        <Button variant="outlined" onClick={() => setOpen(true)}>
          {t('importExport.tripBackup')}
        </Button>
      )}
      
      <MuiDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('importExport.backupAndRestore')}
        maxWidth="sm"
      >
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('importExport.backupDescription')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={exportData} 
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              fullWidth
              disabled={!trip?.id}
            >
              {t('importExport.exportCompleteBackup')}
            </Button>
            <Button 
              onClick={triggerFileInput} 
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              {t('importExport.importBackup')}
            </Button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImport} 
              style={{ display: 'none' }} 
              accept=".json"
            />
          </Box>
        </Box>
      </MuiDialog>
    </>
  );
};

export default ImportExportFeature;
